import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form, InputGroup } from "react-bootstrap";
import { useUpdateCompanyPrice } from "../../hooks/useUpdateCompanyPrice";

const PriceUpdateModal = ({
  show,
  onHide,
  companyId,
  currentPrice,
  plan = null,
}) => {
  const [formData, setFormData] = useState({
    newPrice: "",
    pricePerAdditionalUser: "",
    baseplanUsers: "",
    immediate: false,
    prorate: true,
    notifyEmail: true,
    description: "",
  });
  const initialValuesRef = useRef({});

  const { mutate: updatePrice, isPending } = useUpdateCompanyPrice();

  // Initialize form and track initial values when modal opens
  useEffect(() => {
    if (show) {
      const initialPrice = currentPrice || plan?.baseplanPrice || "";
      const initialPricePerUser = plan?.pricePerAdditionalUser || "";
      const initialBaseUsers = plan?.baseplanUsers || "";

      const initial = {
        newPrice: initialPrice,
        pricePerAdditionalUser: initialPricePerUser,
        baseplanUsers: initialBaseUsers,
        immediate: false,
        prorate: true,
        notifyEmail: true,
        description: "",
      };

      initialValuesRef.current = initial;
      setFormData(initial);
    }
  }, [show, currentPrice, plan]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Helper function to check if a value has changed
  const hasChanged = (field, currentValue) => {
    const initialValue = initialValuesRef.current[field];

    // Handle empty strings and zeros
    const initialStr = String(initialValue || "");
    const currentStr = String(currentValue || "");

    // For numeric fields, compare parsed values
    if (
      field === "newPrice" ||
      field === "pricePerAdditionalUser"
    ) {
      const initialNum = parseFloat(initialStr) || 0;
      const currentNum = parseFloat(currentStr) || 0;
      return initialNum !== currentNum;
    }

    // For baseplanUsers, compare as integers
    if (field === "baseplanUsers") {
      const initialNum = parseInt(initialStr) || 0;
      const currentNum = parseInt(currentStr) || 0;
      return initialNum !== currentNum;
      }

    // For boolean and string fields, compare directly
    return initialValue !== currentValue;
  };

  // Get only changed fields
  const getChangedFields = () => {
    const changed = {};

    // Check numeric fields
    if (hasChanged("newPrice", formData.newPrice) && formData.newPrice) {
      changed.newPrice = parseFloat(formData.newPrice);
    }
    if (
      hasChanged("pricePerAdditionalUser", formData.pricePerAdditionalUser) &&
      formData.pricePerAdditionalUser
    ) {
      changed.pricePerAdditionalUser = parseFloat(
        formData.pricePerAdditionalUser
      );
    }
    if (
      hasChanged("baseplanUsers", formData.baseplanUsers) &&
      formData.baseplanUsers
    ) {
      changed.baseplanUsers = parseInt(formData.baseplanUsers);
    }

    // Check boolean fields
    if (hasChanged("immediate", formData.immediate)) {
      changed.immediate = formData.immediate;
    }
    if (hasChanged("prorate", formData.prorate)) {
      changed.prorate = formData.prorate;
    }
    if (hasChanged("notifyEmail", formData.notifyEmail)) {
      changed.notifyEmail = formData.notifyEmail;
    }

    // Check description
    if (
      hasChanged("description", formData.description) &&
      formData.description.trim()
    ) {
      changed.description = formData.description.trim();
    }

    return changed;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const changedFields = getChangedFields();

    if (Object.keys(changedFields).length === 0) {
      // No changes detected, just close the modal
      onHide();
      return;
    }

    updatePrice(
      {
        companyId,
        body: changedFields,
      },
      {
        onSuccess: () => {
          onHide();
        },
      }
    );
  };

  const handleClose = () => {
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Update Company Price</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Base Price *</Form.Label>
            <InputGroup>
              <InputGroup.Text>$</InputGroup.Text>
            <Form.Control
              type="number"
              step="0.01"
              min="0"
                value={formData.newPrice}
                onChange={(e) => handleInputChange("newPrice", e.target.value)}
              required
                placeholder="Enter base price"
            />
            </InputGroup>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Price Per Additional User</Form.Label>
            <InputGroup>
              <InputGroup.Text>$</InputGroup.Text>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                value={formData.pricePerAdditionalUser}
                onChange={(e) =>
                  handleInputChange("pricePerAdditionalUser", e.target.value)
                }
                placeholder="Enter price per additional user"
              />
            </InputGroup>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Base Plan Users</Form.Label>
            <Form.Control
              type="number"
              min="0"
              value={formData.baseplanUsers}
              onChange={(e) =>
                handleInputChange("baseplanUsers", e.target.value)
              }
              placeholder="Enter number of base plan users"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description (Optional)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              placeholder="Add description (e.g., coupon code, promotional discount, etc.)"
            />
            <Form.Text className="text-muted">
              Add any notes about this price change (e.g., coupon applied,
              promotional offer, etc.)
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Apply immediately"
              checked={formData.immediate}
              onChange={(e) => handleInputChange("immediate", e.target.checked)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Prorate charges"
              checked={formData.prorate}
              onChange={(e) => handleInputChange("prorate", e.target.checked)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Send email notification"
              checked={formData.notifyEmail}
              onChange={(e) =>
                handleInputChange("notifyEmail", e.target.checked)
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={isPending || !formData.newPrice || formData.newPrice <= 0}
          >
            {isPending ? "Updating..." : "Update Price"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default PriceUpdateModal;
